/**
 * Platform Billing Page
 *
 * Superadmin billing dashboard with 5 tabs:
 * - Overview: Platform-wide billing metrics
 * - Enterprises: Enterprise billing aggregates
 * - Partners: Partner billing aggregates
 * - Organizations: Organization subscriptions
 * - Change Requests: Pending subscription changes
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  UsersIcon,
  HomeModernIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { PageTransition, PageTransitionItem } from '@/components/ui/PageTransition';
import { DataTable, ColumnDefinition, ActionDefinition } from '@/components/shared/DataTable';
import {
  useBillingStore,
  PlatformBillingOverview,
  EnterpriseBillingData,
  PartnerBillingData,
  OrganizationBillingData,
  ChangeRequest
} from '@/stores/billingStore';

// ============================================================================
// TYPES
// ============================================================================

type TabType = 'overview' | 'enterprises' | 'partners' | 'organizations' | 'change-requests';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const PlatformBillingPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const {
    platformOverview,
    platformOverviewLoading,
    enterprises,
    enterprisesLoading,
    partners,
    partnersLoading,
    organizations,
    organizationsLoading,
    changeRequests,
    changeRequestsLoading,
    pendingCount,
    fetchPlatformOverview,
    fetchEnterprises,
    fetchPartners,
    fetchOrganizations,
    fetchChangeRequests
  } = useBillingStore();

  // Check permissions
  useEffect(() => {
    if (!authLoading && user) {
      const hasAccess = user.role === 'superadmin' ||
        user.permissions?.some(p =>
          p === 'platform.subscriptions.view' ||
          p === 'platform.*'
        );
      setHasPermission(hasAccess);
    }
  }, [user, authLoading]);

  // Fetch data based on active tab
  useEffect(() => {
    if (hasPermission) {
      switch (activeTab) {
        case 'overview':
          fetchPlatformOverview();
          break;
        case 'enterprises':
          fetchEnterprises();
          break;
        case 'partners':
          fetchPartners();
          break;
        case 'organizations':
          fetchOrganizations();
          break;
        case 'change-requests':
          fetchChangeRequests();
          break;
      }
    }
  }, [activeTab, hasPermission, fetchPlatformOverview, fetchEnterprises, fetchPartners, fetchOrganizations, fetchChangeRequests]);

  // Loading state
  if (authLoading || hasPermission === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-indigo-50/20 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Access denied
  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-indigo-50/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-600 mb-6">
            You don't have permission to access the billing dashboard.
          </p>
          <button
            onClick={() => navigate('/management')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: ChartBarIcon },
    { id: 'enterprises' as const, label: 'Enterprises', icon: BuildingOfficeIcon },
    { id: 'partners' as const, label: 'Partners', icon: UsersIcon },
    { id: 'organizations' as const, label: 'Organizations', icon: HomeModernIcon },
    {
      id: 'change-requests' as const,
      label: 'Change Requests',
      icon: ClipboardDocumentCheckIcon,
      badge: pendingCount > 0 ? pendingCount : undefined
    }
  ];

  return (
    <PageTransition>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-indigo-50/20">
        {/* Header */}
        <PageTransitionItem>
          <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/50 sticky top-0 z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <CurrencyDollarIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Platform Billing</h1>
                  <p className="text-sm text-slate-500">
                    Billing overview and subscription management
                  </p>
                </div>
              </div>
            </div>
          </div>
        </PageTransitionItem>

        {/* Tabs */}
        <PageTransitionItem delay={0.1}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/50 p-1">
              <nav className="flex space-x-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                    {tab.badge && (
                      <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                        activeTab === tab.id
                          ? 'bg-white/20 text-white'
                          : 'bg-red-100 text-red-600'
                      }`}>
                        {tab.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </PageTransitionItem>

        {/* Content */}
        <PageTransitionItem delay={0.2}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {activeTab === 'overview' && (
              <OverviewTab data={platformOverview} loading={platformOverviewLoading} />
            )}
            {activeTab === 'enterprises' && (
              <EnterprisesTab data={enterprises} loading={enterprisesLoading} />
            )}
            {activeTab === 'partners' && (
              <PartnersTab data={partners} loading={partnersLoading} />
            )}
            {activeTab === 'organizations' && (
              <OrganizationsTab data={organizations} loading={organizationsLoading} />
            )}
            {activeTab === 'change-requests' && (
              <ChangeRequestsTab data={changeRequests} loading={changeRequestsLoading} />
            )}
          </div>
        </PageTransitionItem>
      </div>
    </PageTransition>
  );
};

// ============================================================================
// TAB COMPONENTS
// ============================================================================

const OverviewTab: React.FC<{ data: PlatformBillingOverview | null; loading: boolean }> = ({ data, loading }) => {
  if (loading) {
    return <LoadingState message="Loading billing overview..." />;
  }

  if (!data) {
    return <EmptyState message="No billing data available" />;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Monthly Revenue (MRR)"
          value={`$${data.totalMRR.toLocaleString()}`}
          icon={CurrencyDollarIcon}
          color="purple"
        />
        <StatCard
          title="Active Subscriptions"
          value={data.totalActiveSubscriptions.toLocaleString()}
          icon={CheckCircleIcon}
          color="emerald"
        />
        <StatCard
          title="Total Organizations"
          value={data.totalOrganizations.toLocaleString()}
          icon={HomeModernIcon}
          color="blue"
        />
        <StatCard
          title="Overage Revenue"
          value={`$${data.overageRevenue.toLocaleString()}`}
          icon={ArrowTrendingUpIcon}
          color="amber"
        />
      </div>

      {/* Tier Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/50">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Revenue by Tier</h3>
          <div className="space-y-4">
            {data.tierBreakdown.map((tier) => (
              <div key={tier.tier} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${getTierColor(tier.tier)}`} />
                  <span className="text-sm font-medium text-slate-700 capitalize">{tier.tier}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-500">{tier.count} orgs</span>
                  <span className="text-sm font-semibold text-slate-800">${tier.mrr.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/50">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Subscription Status</h3>
          <div className="space-y-4">
            {data.statusBreakdown.map((status) => (
              <div key={status.status} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(status.status)}`} />
                  <span className="text-sm font-medium text-slate-700 capitalize">{status.status}</span>
                </div>
                <span className="text-sm font-semibold text-slate-800">{status.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const EnterprisesTab: React.FC<{ data: EnterpriseBillingData[]; loading: boolean }> = ({ data, loading }) => {
  if (loading) {
    return <LoadingState message="Loading enterprises..." />;
  }

  const columns: ColumnDefinition<EnterpriseBillingData>[] = [
    { id: 'name', header: 'Enterprise', accessor: (row) => row.name, sortable: true },
    {
      id: 'status',
      header: 'Status',
      accessor: (row) => <StatusBadge status={row.status} />
    },
    {
      id: 'partnersCount',
      header: 'Partners',
      accessor: (row) => `${row.partnersCount}/${row.partnersQuota === -1 ? '∞' : row.partnersQuota}`
    },
    {
      id: 'organizationsCount',
      header: 'Organizations',
      accessor: (row) => `${row.organizationsCount}/${row.organizationsQuota === -1 ? '∞' : row.organizationsQuota}`
    },
    {
      id: 'totalMRR',
      header: 'MRR',
      sortable: true,
      accessor: (row) => `$${row.totalMRR.toLocaleString()}`
    },
    { id: 'activeSubscriptions', header: 'Active Subs', accessor: (row) => row.activeSubscriptions, sortable: true }
  ];

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/50">
      <DataTable<EnterpriseBillingData>
        data={data}
        columns={columns}
        emptyMessage="No enterprises found"
      />
    </div>
  );
};

const PartnersTab: React.FC<{ data: PartnerBillingData[]; loading: boolean }> = ({ data, loading }) => {
  if (loading) {
    return <LoadingState message="Loading partners..." />;
  }

  const columns: ColumnDefinition<PartnerBillingData>[] = [
    { id: 'name', header: 'Partner', accessor: (row) => row.name, sortable: true },
    { id: 'enterpriseName', header: 'Enterprise', accessor: (row) => row.enterpriseName || '-' },
    {
      id: 'status',
      header: 'Status',
      accessor: (row) => <StatusBadge status={row.status} />
    },
    {
      id: 'organizationsCount',
      header: 'Organizations',
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <span>{row.organizationsCount}/{row.organizationsQuota === -1 ? '∞' : row.organizationsQuota}</span>
          {row.quotaStatus.percent >= 80 && (
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
          )}
        </div>
      )
    },
    {
      id: 'totalMRR',
      header: 'MRR',
      sortable: true,
      accessor: (row) => `$${row.totalMRR.toLocaleString()}`
    },
    { id: 'activeSubscriptions', header: 'Active Subs', accessor: (row) => row.activeSubscriptions, sortable: true }
  ];

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/50">
      <DataTable<PartnerBillingData>
        data={data}
        columns={columns}
        emptyMessage="No partners found"
      />
    </div>
  );
};

const OrganizationsTab: React.FC<{ data: OrganizationBillingData[]; loading: boolean }> = ({ data, loading }) => {
  if (loading) {
    return <LoadingState message="Loading organizations..." />;
  }

  const columns: ColumnDefinition<OrganizationBillingData>[] = [
    { id: 'name', header: 'Organization', accessor: (row) => row.name, sortable: true },
    { id: 'partnerName', header: 'Partner', accessor: (row) => row.partnerName || '-' },
    {
      id: 'subscription',
      header: 'Plan',
      accessor: (row) => row.subscription?.planName || <span className="text-slate-400">No Plan</span>
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row) => row.subscription ? <StatusBadge status={row.subscription.status} /> : '-'
    },
    {
      id: 'usage',
      header: 'Usage',
      accessor: (row) => {
        if (!row.subscription) return '-';
        const usage = row.subscription.usage;
        const maxPercent = Math.max(usage.encounters.percent, usage.minutes.percent);
        return (
          <div className="flex items-center gap-2">
            <div className="w-16 bg-slate-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${maxPercent >= 90 ? 'bg-red-500' : maxPercent >= 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(maxPercent, 100)}%` }}
              />
            </div>
            <span className="text-xs text-slate-500">{maxPercent}%</span>
          </div>
        );
      }
    },
    {
      id: 'monthlyAmount',
      header: 'Monthly',
      sortable: true,
      accessor: (row) => row.subscription ? `$${row.subscription.monthlyAmount.toLocaleString()}` : '-'
    }
  ];

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/50">
      <DataTable<OrganizationBillingData>
        data={data}
        columns={columns}
        emptyMessage="No organizations found"
      />
    </div>
  );
};

const ChangeRequestsTab: React.FC<{ data: ChangeRequest[]; loading: boolean }> = ({ data, loading }) => {
  const { approveChangeRequest, rejectChangeRequest, fetchChangeRequests } = useBillingStore();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleApprove = async (id: string) => {
    const password = prompt('Enter your password to approve this change request:');
    if (!password) return;

    setActionLoading(id);
    try {
      await approveChangeRequest(id, password);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    const reason = prompt('Enter a reason for rejecting this change request:');
    if (!reason) return;

    setActionLoading(id);
    try {
      await rejectChangeRequest(id, reason);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to reject');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return <LoadingState message="Loading change requests..." />;
  }

  const columns: ColumnDefinition<ChangeRequest>[] = [
    { id: 'organizationName', header: 'Organization', accessor: (row) => row.organizationName, sortable: true },
    { id: 'currentPlanName', header: 'Current Plan', accessor: (row) => row.currentPlanName || 'None' },
    { id: 'requestedPlanName', header: 'Requested Plan', accessor: (row) => row.requestedPlanName },
    {
      id: 'requestType',
      header: 'Type',
      accessor: (row) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
          row.requestType === 'upgrade' ? 'bg-emerald-100 text-emerald-700' :
          row.requestType === 'downgrade' ? 'bg-amber-100 text-amber-700' :
          'bg-slate-100 text-slate-700'
        }`}>
          {row.requestType}
        </span>
      )
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (row) => <StatusBadge status={row.status} />
    },
    {
      id: 'createdAt',
      header: 'Requested',
      accessor: (row) => new Date(row.createdAt).toLocaleDateString()
    }
  ];

  const actions: ActionDefinition<ChangeRequest>[] = [
    {
      id: 'approve',
      tooltip: 'Approve',
      icon: <CheckCircleIcon className="h-4 w-4" />,
      variant: 'success',
      onClick: (row) => handleApprove(row.id),
      disabled: (row) => row.status !== 'pending' || actionLoading === row.id
    },
    {
      id: 'reject',
      tooltip: 'Reject',
      icon: <XCircleIcon className="h-4 w-4" />,
      variant: 'danger',
      onClick: (row) => handleReject(row.id),
      disabled: (row) => row.status !== 'pending' || actionLoading === row.id
    }
  ];

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/50">
      <DataTable<ChangeRequest>
        data={data}
        columns={columns}
        actions={actions}
        emptyMessage="No change requests found"
      />
    </div>
  );
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const StatCard: React.FC<{
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'purple' | 'emerald' | 'blue' | 'amber';
}> = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    purple: 'bg-purple-100 text-purple-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600'
  };

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-slate-200/50">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    trial: 'bg-blue-100 text-blue-700',
    suspended: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-700',
    pending: 'bg-slate-100 text-slate-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const LoadingState: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-12 border border-slate-200/50 flex flex-col items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent mb-4" />
    <p className="text-slate-600">{message}</p>
  </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-12 border border-slate-200/50 flex flex-col items-center justify-center text-center">
    <ChartBarIcon className="w-12 h-12 text-slate-400 mb-4" />
    <p className="text-slate-500">{message}</p>
  </div>
);

const getTierColor = (tier: string): string => {
  const colors: Record<string, string> = {
    basic: 'bg-blue-500',
    professional: 'bg-purple-500',
    enterprise: 'bg-amber-500',
    custom: 'bg-slate-500'
  };
  return colors[tier] || 'bg-slate-500';
};

const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    active: 'bg-emerald-500',
    trial: 'bg-blue-500',
    suspended: 'bg-amber-500',
    cancelled: 'bg-red-500',
    pending: 'bg-slate-500'
  };
  return colors[status] || 'bg-slate-500';
};

export default PlatformBillingPage;
